/* eslint-disable no-console */

import Two from 'two.js';
import { Group } from 'two.js/src/group';
import { Easing, Tween } from '@tweenjs/tween.js';
import type { RefObject } from 'react';
import {
  DBGame,
  EliminationEvent,
  GiftsEvent,
  ReAppearEvent,
  WheelEvent
} from '@lykkehjulet/db-model';
import {
  accumulatedAngleSum,
  accumulatedTicketSum,
  commonStartAngle,
  createDiscoLight,
  createOuterBorder,
  createTicketEdgeLine,
  createWedgeBackground,
  createWedgeText,
  drawHand,
  drawTicker, drawTrash,
  getNumTickets,
  getRandomCoordinatesInCircle
} from 'rf-ui/GameRenderer/rendererHelper';
import {
  shrinkTicket,
  tweenHand,
  tweenHandFake, tweenHandReroll
} from 'rf-ui/GameRenderer/wheelAnimatior';
import {
  createBalloon, createConfetti, createCrown, createDiscoball
} from 'rf-ui/GameRenderer/spritesFactory';
import { SpinProvider, SpinProviderFactory } from 'rf-ui/GameRenderer/spinProvider';
import TwoScene from 'rf-ui/GameRenderer/TwoScene';
import { Line } from 'two.js/src/shapes/line';
import { Text } from 'two.js/src/text';
import { ArcSegment } from 'two.js/src/shapes/arc-segment';
import TicketAnimator from 'rf-ui/GameRenderer/TicketAnimator';
import Ticket from 'rf-ui/GameRenderer/entities/ticket';
import Coffin from 'rf-ui/GameRenderer/entities/Coffin';
import WrappedTicket from 'rf-ui/GameRenderer/entities/WrappedTicket';

const parameters = {
  wheelRadius: 320,
  textPos: 0.95,
  crownPos: 1.03,
  hand: {
    end: {
      x: 300,
      y: -420
    },
    mid: {
      x: 330,
      y: -462
    },
    start: {
      x: 500,
      y: -700
    },
    reRollEnd: {
      x: 200,
      y: -700
    }
  },
  spinningExitments: {
    photoFinish: {
      whenUnder: 6,
      chance: 0.6
    }
  },
  chances: {
    counterClockwise: 0.1
  },
  crownColors: {
    gold: 'rgb(212,175,55)',
    silver: 'rgb(192,192,192)',
    copper: 'rgb(205,127,50)',
    others: 'rgb(0,0,0)',
  }
};

function getCrownColorForPosition(position: string | number): string {
  switch (parseInt(`${position}`)) {
    case 1:
      return parameters.crownColors.gold;
    case 2:
      return parameters.crownColors.silver;
    case 3:
      return parameters.crownColors.copper;
    default:
      return parameters.crownColors.others;
  }
}

function hashCode(str: string): number {
  return str.split('')
    .reduce((prevHash, currVal) => // eslint-disable-next-line no-bitwise
      (((prevHash << 5) - prevHash) + currVal.charCodeAt(0)) | 0, 0);
}

export type WheelPlayer = {
  playerId: string;
  playerName: string;
  color: string;
};

type WheelPlayerWithTickets = WheelPlayer & {
  tickets: number;
};

type RemainingPlayer = {
  playerId: string;
  tickets: number;
};

type DrawnTicketCrown = {
  element: Group;
  position: number;
};

export type DrawnTicket = {
  angle: number;
  wedge: Ticket;
  line: Line;
  crown: DrawnTicketCrown | null;
};

type DrawnPlayerElements = Record<string, {
  tickets: DrawnTicket[];
  nameElement: Text | null;
  name: string;
}>;

export default class WheelOfFortuneRenderer extends TwoScene {
  curRotation: number = 0;

  gameId: string;

  game: DBGame;

  onAnimationFinished: (roundNumber: number) => void;

  ticker: Group | null = null;

  hand: ReturnType<typeof drawHand> | null = null;

  trash: ReturnType<typeof drawTrash> | null = null;

  wheel: Group | null = null;

  wheelGroup: Group | null = null;

  outerWheelGroup: Group | null = null;

  allPlayers: WheelPlayer[];

  remainingPlayers: RemainingPlayer[];

  currentWinners: string[] | null = null;

  drawnPlayerElements: DrawnPlayerElements = {};

  lastAnimationStarted: number | null = null;

  numberOfPrizes: number;

  spinProvider: SpinProvider;

  ticketAnimator: TicketAnimator;

  constructor(
    gameId: string,
    game: any,
    domElement: RefObject<HTMLDivElement | null>,
    allPlayers: WheelPlayer[],
    remainingPlayers: RemainingPlayer[],
    numberOfPrizes: number,
    onAnimationFinished: (roundNumber: number) => void,
  ) {
    super(domElement);
    this.gameId = gameId;
    this.game = game;
    this.allPlayers = allPlayers;
    this.remainingPlayers = remainingPlayers;
    this.numberOfPrizes = numberOfPrizes;
    this.onAnimationFinished = onAnimationFinished;
    this.spinProvider = SpinProviderFactory.create(this.game);
    this.ticketAnimator = new TicketAnimator(this, parameters);

    this.setup();
  }

  setupScene(): Group {
    this.curRotation = Math.abs(hashCode(this.gameId)) % (Math.PI * 2);

    this.outerWheelGroup = this.two.makeGroup();
    this.wheel = this.drawWheelGroup(this.allPlayers, this.remainingPlayers, []);
    this.wheelGroup!.translation.set(0, 0);
    this.outerWheelGroup.add(this.wheel);
    this.ticker = drawTicker(this.two);
    this.ticker.translation.set(0, -parameters.wheelRadius - 15);
    this.hand = drawHand(this.two);
    this.hand.translation.set(parameters.hand.start.x, parameters.hand.start.y);
    this.hand.opacity = 0;
    /*
    const debugWrappedTickets = Array.from({ length: 10 }, (_, index) => {
      const wrapped = new WrappedTicket(this.two, 0, Math.PI * 0.3).massCentered();
      const x = (index - ((10 - 1) / 2)) * 200;
      wrapped.translation.set(x, 100);
      return wrapped;
    });
    const tween = new Tween({size: Math.PI * 0.3}).to({size: Math.PI * 1.1},2000)
      .yoyo(true)
      .repeat(100)
      .onUpdate((p) => {
        debugWrappedTickets.forEach((wrapped) => {
          wrapped.resize(1.2, p.size);
        });
      })
      .start()
   // this.tweens.add(tween);
     */
    return this.two.makeGroup(
      this.outerWheelGroup,
      this.ticker,
      this.hand,
      this.trash
    );
  }

  mapPlayersWithTickets = (allPlayers: WheelPlayer[], remainingPlayers: RemainingPlayer[]): WheelPlayerWithTickets[] => {
    const ticketsByPlayerId = remainingPlayers.reduce((acc, remainingPlayer) => {
      acc[remainingPlayer.playerId] = remainingPlayer.tickets;
      return acc;
    }, {} as Record<string, number>);

    return allPlayers.map(player => ({
      ...player,
      tickets: (ticketsByPlayerId[player.playerId] || 0)
    }));
  };

  getDrawnTicketsInOrder = (): DrawnTicket[] => Object.values(this.drawnPlayerElements)
    .flatMap(drawnElements => drawnElements.tickets);

  drawWheelGroup = (allPlayers: WheelPlayer[], remainingPlayers: RemainingPlayer[], winners: string[]) => {
    const winnersLeft = [...winners].map((val, index) => ({
      pos: index + 1,
      winner: val
    }));
    this.drawnPlayerElements = {};
    allPlayers.forEach(player => {
      this.drawnPlayerElements[player.playerId] = {
        tickets: [],
        nameElement: null,
        name: player.playerName
      };
    });
    this.wheelGroup = this.two.makeGroup();

    const playersWithTickets: WheelPlayerWithTickets[] = this.mapPlayersWithTickets(allPlayers, remainingPlayers);
    const totalTickets = getNumTickets(playersWithTickets);
    if (totalTickets <= 0) {
      const outerCircleBorder = createOuterBorder(parameters, this.two);
      this.wheelGroup.add(outerCircleBorder);
      this.wheelGroup.rotation = this.curRotation;
      return this.wheelGroup;
    }
    const wedgeAngle = (Math.PI * 2.0) / totalTickets;
    const wedgeAngles = Array(totalTickets)
      .fill(wedgeAngle);

    const accWedgeAngles = accumulatedAngleSum(wedgeAngles);

    const accSums = accumulatedTicketSum(playersWithTickets);
    const wheelElements: any[] = [];
    accWedgeAngles.forEach((current, index) => {
      const startAngle = index > 0 ? accWedgeAngles[index - 1] : commonStartAngle;
      const centerAngle = startAngle + ((current - startAngle) / 2);
      const playerIndex = accSums.findIndex(ticketsSum => ticketsSum > index);
      if (playerIndex < 0) return;

      const { playerId } = playersWithTickets[playerIndex];
      let crown = null;
      const winnerIndex = winnersLeft.findIndex(winner => winner.winner === playerId);
      if (winnerIndex >= 0) {
        const position = winnersLeft[winnerIndex].pos;
        const crownElement = createCrown(this.two, {
          text: `${position}`,
          color: getCrownColorForPosition(position)
        });
        crownElement.translation.set(Math.cos(centerAngle) * (parameters.wheelRadius * parameters.crownPos), Math.sin(centerAngle) * (parameters.wheelRadius * parameters.crownPos));
        crownElement.rotation = centerAngle + Math.PI / 2;
        crown = {
          element: crownElement,
          position
        };
        winnersLeft.splice(winnerIndex, 1);
      }

      const wedge = new Ticket(
        this.two,
        startAngle,
        current - startAngle,
        playersWithTickets[playerIndex].color,
        playersWithTickets[playerIndex].playerName,
        false,
        playersWithTickets[playerIndex].playerId
      );
      const edgeLineOpacity = Math.max(0, 1 - ((totalTickets - 10) * 0.1));
      const edgeLine = createTicketEdgeLine(startAngle, edgeLineOpacity, parameters, this.two);

      this.drawnPlayerElements[playersWithTickets[playerIndex].playerId].tickets.push({
        angle: centerAngle,
        wedge,
        line: edgeLine,
        crown
      });
      wheelElements.push(wedge);
      wheelElements.push(edgeLine);
      if (crown) wheelElements.push(crown.element);
    });

    const outerCircleBorder = createOuterBorder(parameters, this.two);
    this.wheelGroup.add(outerCircleBorder);
    this.wheelGroup.add(wheelElements);

    this.wheelGroup.rotation = this.curRotation;

    return this.wheelGroup;
  };

  updateCurrentPlayers = (allPlayers: WheelPlayer[], remainingPlayers: RemainingPlayer[], winners: string[] | null) => {
    if (JSON.stringify(this.allPlayers) !== JSON.stringify(allPlayers)
      || JSON.stringify(this.remainingPlayers) !== JSON.stringify(remainingPlayers)
      || JSON.stringify(winners) !== JSON.stringify(this.currentWinners)) {
      this.allPlayers = allPlayers;
      this.remainingPlayers = remainingPlayers;
      this.currentWinners = winners;
      if (this.wheel) {
        Object.values(this.drawnPlayerElements).forEach(playerElement => {
          playerElement.tickets.forEach(ticket => {
            ticket.line.remove();
            // @ts-ignore
            ticket.line = null;
            if (ticket.crown) {
              ticket.crown.element.remove();
              // @ts-ignore
              ticket.crown = null;
            }
            ticket.wedge.destroy();
            ticket.wedge.remove();
            // @ts-ignore
            ticket.wedge = null;
          });
          playerElement.tickets = [];
          playerElement.nameElement = null;
        });
        this.wheel.remove();
        this.wheel = null;
        this.drawnPlayerElements = {};
      }
      this.wheel = this.drawWheelGroup(this.allPlayers, this.remainingPlayers, winners || []);
      this.outerWheelGroup!.add(this.wheel);

      if (winners && winners.length && this.lastAnimationStarted) {
        this.animateWinners();
      }
    }
  };

  createDetachedTicketGroup(ticket: DrawnTicket, name: string): Group {
    /*
    const size = ticket.wedgeBackground.endAngle - ticket.wedgeBackground.startAngle;
    const wedge = createWedgeBackground(commonStartAngle, commonStartAngle + size, ticket.wedgeBackground.fill as string, parameters, this.two);
    wedge.stroke = '#fff';
    wedge.linewidth = 2;
    const text = createWedgeText(commonStartAngle, commonStartAngle + size, name, parameters);

    const shift = {
      x: Math.tan(size / 2) * (parameters.wheelRadius * 0.5),
      y: -parameters.wheelRadius * 0.5
    };
    wedge.translation.x -= shift.x;
    wedge.translation.y -= shift.y;
    text.translation.x -= shift.x;
    text.translation.y -= shift.y;

    const wedgeGroup = this.two.makeGroup();
    this.scene!.add(wedgeGroup);
    wedgeGroup.add(wedge);
    wedgeGroup.add(text);
    wedgeGroup.translation.x = this.wheelGroup!.translation.x + shift.x;
    wedgeGroup.translation.y = this.wheelGroup!.translation.y + shift.y;

    return wedgeGroup;

     */
  }

  animateWinners(): void {
    this.animateCrowns();

    const confettiTimeout = setTimeout(() => {
      this.createConfetti();
    }, 2000);
    const balloonTimeout = setTimeout(() => {
      this.createBaloons();
    }, 3000);
    const discoLightTimeout = setTimeout(() => {
      this.createDiscoLight();
    }, 3000);
    this.timeouts.push(confettiTimeout, balloonTimeout, discoLightTimeout);
  }

  animateCrowns(): void {
    Object.values(this.drawnPlayerElements)
      .forEach(playerElement =>
        playerElement.tickets.forEach(ticket => {
          if (ticket.crown) {
            // eslint-disable-next-line no-param-reassign
            ticket.crown!.element.opacity = 0;
            const landTween = new Tween({ offset: 100 })
              .delay(-300 + ticket.crown!.position * 300)
              .to({ offset: 0 }, 3000)
              .onUpdate(proxy => {
                ticket.crown!.element.translation.set(Math.cos(ticket.crown!.element.rotation - Math.PI / 2) * (parameters.wheelRadius * parameters.crownPos + proxy.offset), Math.sin(ticket.crown!.element.rotation - Math.PI / 2) * (parameters.wheelRadius * parameters.crownPos + proxy.offset));
              })
              .easing(Easing.Exponential.Out)
              .start();

            const fadeInTween = new Tween({ opacity: 0 })
              .delay(-300 + ticket.crown!.position * 300)
              .to({ opacity: 1 }, 700)
              .onUpdate(proxy => {
                // eslint-disable-next-line no-param-reassign
                ticket.crown!.element.opacity = proxy.opacity;
              })
              .easing(Easing.Quadratic.Out)
              .start();

            landTween.isPaused();
            this.tweens.add(landTween, fadeInTween);
          }
        }));
  }

  createDiscoLight(): void {
    const discoBall = createDiscoball(this.two);
    const discoLight = createDiscoLight(this.two);
    const discoBallGroup = this.two.makeGroup(discoBall, discoLight);

    discoBallGroup.translation.x = 150;
    const startY = -150;
    const loweredY = 60;

    const liftTween = new Tween({
      y: loweredY,
      opacity: 1
    })
      .delay(5000)
      .to({
        y: startY,
        opacity: 0
      }, 2000)
      .onUpdate(proxy => {
        discoBallGroup.translation.y = proxy.y;
        discoBallGroup.opacity = proxy.opacity;
      })
      .onComplete(() => {
        discoBallGroup.remove();
      });

    const lowerTween = new Tween({ y: startY })
      .to({ y: loweredY }, 1000)
      .onUpdate(proxy => {
        discoBallGroup.translation.y = proxy.y;
      })
      .chain(liftTween)
      .start();

    this.tweens.add(lowerTween, liftTween);

    const rotateDiscoLightTween = new Tween({ rotation: 0 })
      .to({ rotation: Math.PI * 2 }, 4000)
      .onUpdate(proxy => {
        discoLight.rotation = proxy.rotation;
      })
      .repeat(1000)
      .start();

    this.tweens.add(rotateDiscoLightTween);
  }

  createConfetti(): void {
    Array.from({ length: 200 })
      .forEach(() => {
        const colors = ['rgb(78,187,246)', 'rgb(239,37,37)', 'rgb(255,218,65)', 'rgb(78,246,81)'];

        const hitSize = Math.min(this.two.width, this.two.height) * 0.6;
        const hitArea = getRandomCoordinatesInCircle(hitSize);

        const targetPos = {
          x: this.two.width * 0.5 + hitArea.x,
          y: this.two.height * 0.5 + hitArea.y,
        };

        const confetti = createConfetti(this.two, {
          scale: 0.8 + 0.4 * Math.random(),
          color: colors[Math.floor(Math.random() * colors.length)]
        });

        const dropDownTween = new Tween({
          x: targetPos.x,
          y: targetPos.y
        })
          .to({
            x: targetPos.x - Math.random() * 500,
            y: targetPos.y + this.two.height + 100
          }, 7000)
          .onUpdate(proxy => {
            confetti.translation.x = proxy.x;
            confetti.translation.y = proxy.y;
          });

        const dropDownFlipTween = Math.random() > 0.5 ? new Tween({ yScale: -1 })
          .to({ yScale: 1 }, 300 + Math.random() * 100)
          .onUpdate(proxy => {
            confetti.scale = new Two.Vector(1, proxy.yScale);
          })
          .repeat(100)
          .yoyo(true) : null;

        const shootInTween = new Tween({
          x: this.two.width,
          y: this.two.height
        })
          .to(targetPos, 500 + Math.random() * 500)
          .easing(Easing.Quadratic.Out)
          .onUpdate(proxy => {
            confetti.translation.x = proxy.x;
            confetti.translation.y = proxy.y;
          })
          .chain(...[dropDownTween, dropDownFlipTween].filter((t): t is Tween<any> => t !== null))
          .start();

        this.tweens.add(shootInTween, dropDownTween);
        if (dropDownFlipTween) {
          this.tweens.add(dropDownFlipTween);
        }
      });
  }

  createBaloons(): void {
    Array.from({ length: 20 })
      .forEach(() => {
        const colors = ['rgb(78,187,246)', 'rgb(239,37,37)', 'rgb(255,218,65)', 'rgb(78,246,81)'];
        const balloon = createBalloon(this.two, { color: colors[Math.floor(Math.random() * colors.length)] });
        balloon.translation.set(Math.random() * this.two.width, Math.random() * this.two.height + this.two.height * 0.5);
        balloon.scale = 0.8 + Math.random() * 0.4;

        const animTime = 5000 + Math.random() * 5000;
        const fadeIn = new Tween({ opacity: 0 })
          .to({ opacity: 1 }, 500)
          .onUpdate(proxy => {
            balloon.opacity = proxy.opacity;
          })
          .start();
        const tweenUp = new Tween({ y: balloon.translation.y })
          .to({ y: balloon.translation.y - 1500 }, animTime)
          .onUpdate(proxy => {
            balloon.translation.y = proxy.y;
          })
          .onComplete(() => {
            balloon.remove();
          })
          .start();
        const tweenWobble = new Tween({ x: balloon.translation.x })
          .to({ x: balloon.translation.x - 10 }, animTime / 10)
          .onUpdate(proxy => {
            balloon.translation.x = proxy.x;
          })
          .easing(Easing.Quadratic.InOut)
          .yoyo(true)
          .repeat(10)
          .repeatDelay(100)
          .start();
        this.tweens.add(fadeIn, tweenUp, tweenWobble);
      });
  }

  animateExplodingStar(x: number, y: number) {
    const scale = 3;
    const sharpness = 5;
    const star = this.two.makePath([new Two.Anchor(0, -sharpness * scale), new Two.Anchor(scale, -1 * scale), new Two.Anchor(sharpness * scale, 0), new Two.Anchor(scale, scale), new Two.Anchor(0, sharpness * scale), new Two.Anchor(-1 * scale, scale), new Two.Anchor(-sharpness * scale, 0), new Two.Anchor(-1 * scale, -1 * scale), new Two.Anchor(0, -sharpness * scale)]);
    star.translation.set(x, y);
    star.stroke = 'none';

    const gradient = new Two.RadialGradient(0, 0, scale * 5, [new Two.Stop(0, 'rgb(255, 255, 255)', 1), new Two.Stop(1, 'rgb(255, 255, 0)', 0.2)]);
    gradient.units = 'userSpaceOnUse';
    star.fill = gradient;
    star.opacity = 0;
    this.scene!.add(star);

    const fadeOutTween = new Tween({ opacity: 1 })
      .delay(100)
      .to({ opacity: 0 }, 200)
      .onUpdate(proxy => {
        star.opacity = proxy.opacity;
      })
      .easing(Easing.Exponential.In);

    const fadeInTween = new Tween({ opacity: 0.2 })
      .delay(100)
      .to({ opacity: 1 }, 50)
      .onUpdate(proxy => {
        star.opacity = proxy.opacity;
      })
      .chain(fadeOutTween)
      .easing(Easing.Exponential.Out)
      .start();

    this.tweens.add(fadeInTween, fadeOutTween);
  }

  animateElimination(event: EliminationEvent, roundNumber: number, isBehind: boolean) {

    const { playerId } = event;

    const numTickets = getNumTickets(this.mapPlayersWithTickets(this.allPlayers, this.remainingPlayers));
    const roundsLeft = numTickets - this.numberOfPrizes;
    const spin = this.spinProvider.getRandomSpin(
      roundsLeft,
      parameters.chances.counterClockwise,
      isBehind
    );

    const curRounds = Math.floor(this.curRotation / (Math.PI * 2));
    const ticket = this.drawnPlayerElements[playerId].tickets[Math.floor(Math.random() * this.drawnPlayerElements[playerId].tickets.length)];
    const disappearingAngle = ticket.wedge.width;
    const roundOffset = spin.clockWise ? (curRounds + (spin.wholeRounds || 5)) : (curRounds - (spin.wholeRounds || 5));
    const edgeDist = 0.49;
    let randomAngleFactor: number;
    if (parameters.spinningExitments.photoFinish.whenUnder > roundsLeft && Math.random() < parameters.spinningExitments.photoFinish.chance) {
      randomAngleFactor = Math.random() > 0.5 ? 0 : 1;
    } else {
      randomAngleFactor = Math.random();
    }
    const angleAfterSpin = commonStartAngle - (ticket.angle) + (roundOffset * (Math.PI * 2))
      - (disappearingAngle * edgeDist) + (randomAngleFactor * disappearingAngle * edgeDist * 2);
    const { name } = this.drawnPlayerElements[playerId];

    const randomAnimation = this.ticketAnimator.getRandomAnimation(disappearingAngle);
    const wedge = new Ticket(
      this.two,
      commonStartAngle,
      disappearingAngle,
      ticket.wedge.color,
      name,
      true,
      playerId
    ).massCentered();
    this.scene?.add(wedge);
    wedge.setTranslationShifted(this.wheelGroup!.translation.x, this.wheelGroup!.translation.y);


    wedge.visible = false;
    const shrinkDuration = 1000;
    const finishDelay = Math.max(0, randomAnimation.animationTime + 100 - shrinkDuration);

    const finishRoundTween = new Tween({ t: 0 })
      .to({ t: 1 }, finishDelay)
      .onComplete(() => {
        this.onAnimationFinished(roundNumber);
      });

    const shrinkProxy = {
      size: 1,
      rotation: angleAfterSpin
    };
    const shrinkTicketsTween = new Tween(shrinkProxy).to({
      size: 0,
      rotation: angleAfterSpin - disappearingAngle
    })
      .duration(shrinkDuration)
      .onStart(() => {
        wedge.visible = true;
        this.ticketAnimator.performAnimation(randomAnimation.animationName, disappearingAngle, wedge);
      })
      .onUpdate(() => {
        shrinkTicket.call(this, shrinkProxy, numTickets, ticket, parameters);
      })
      .chain(finishRoundTween);

    const flickerTween = new Tween({ t: 0 })
      .to({ t: 1 }, 300)
      .onStart(() => {
        // ticket.wedgeBackground.visible = true;
      })
      .onUpdate(proxy => {
        const toggles = Math.floor(proxy.t * 4);
        // ticket.wedgeBackground.visible = toggles % 2 === 0;
        if (toggles % 2 === 0) {
          ticket.wedge.setFlash(true);
        } else {
          ticket.wedge.setFlash(false);
        }
      })
      .onComplete(() => {
        ticket.wedge.setFlash(false);
        // ticket.wedgeBackground.visible = true;
      })
      .chain(shrinkTicketsTween);

    const speedWheelTween = new Tween({ rotation: this.curRotation })
      .to({ rotation: angleAfterSpin }, spin.time)
      .easing(spin.easing)
      .onUpdate(proxy => {
        this.curRotation = proxy.rotation % (Math.PI * 2);
        this.wheel!.rotation = proxy.rotation % (Math.PI * 2);
      })
      .chain(flickerTween)
      .start();

    if (spin.hand === 'real') {
      tweenHand.call(this, this.hand!, spin.time, parameters);
    } else if (spin.hand === 'fake') {
      tweenHandFake.call(this, this.hand!, spin.time - 2000, parameters);
    } else if (spin.hand === 're-roll') {
      tweenHandReroll.call(this, this.hand!, 21_700);
    }

    if (spin.ticker) {
      const tickerRest = spin.clockWise ? -1 : 1;
      const tickerSpinAngle = spin.clockWise ? -Math.PI * 2 : Math.PI * 2;

      const tickerFallTween = new Tween({ rotation: tickerRest * 1.2 }).to({ rotation: 0 }, 500)
        .easing(Easing.Cubic.In)
        .onUpdate(proxy => {
          this.ticker!.rotation = proxy.rotation;
        });

      const tickerTouchTween = new Tween({ rotation: tickerRest })
        .to({ rotation: tickerRest * 1.2 }, 100)
        .easing(Easing.Linear.InOut)
        .repeat((spin.time - 2_000) / 100)
        .yoyo(true)
        .onUpdate(proxy => {
          this.ticker!.rotation = proxy.rotation;
        })
        .chain(tickerFallTween);

      const tickerSpinAroundTween = new Tween({ rotation: 0 })
        .to({ rotation: tickerSpinAngle + tickerRest }, 500)
        .easing(Easing.Linear.InOut)
        .onUpdate(proxy => {
          this.ticker!.rotation = proxy.rotation;
        })
        .chain(tickerTouchTween)
        .start();
      this.tweens.add(tickerFallTween, tickerTouchTween, tickerSpinAroundTween);
    }

    this.tweens.add(speedWheelTween, flickerTween, shrinkTicketsTween, finishRoundTween);
  }

  animateGift(event: GiftsEvent, roundNumber: number) {
    if (!this.scene || !this.wheel) {
      this.onAnimationFinished(roundNumber);
      return;
    }

    const playersBefore: WheelPlayerWithTickets[] = this.mapPlayersWithTickets(this.allPlayers, this.remainingPlayers);
    const numTicketsBefore = getNumTickets(playersBefore);
    if (numTicketsBefore <= 0) {
      this.onAnimationFinished(roundNumber);
      return;
    }

    const giftCountsByPlayerId = event.playerIds.reduce((acc, playerId) => {
      acc[playerId] = (acc[playerId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const giftTargets = this.allPlayers
      .flatMap((player, playerIndex) => {
        const giftCount = giftCountsByPlayerId[player.playerId] || 0;
        if (giftCount <= 0) return [];

        const ticketsBeforeTargetPlayer = playersBefore
          .slice(0, playerIndex)
          .reduce((sum, currentPlayer) => sum + Number(currentPlayer.tickets), 0);
        const targetPlayerTicketsBefore = Number(playersBefore[playerIndex]?.tickets || 0);
        const insertionIndex = ticketsBeforeTargetPlayer + targetPlayerTicketsBefore;

        return Array.from({ length: giftCount }, () => ({
          playerId: player.playerId,
          insertionIndex
        }));
      });

    if (giftTargets.length === 0) {
      this.onAnimationFinished(roundNumber);
      return;
    }

    const startTicketSize = Math.PI * 0.23;
    const targetTicketSize = (Math.PI * 2.0) / (numTicketsBefore + giftTargets.length);
    const ticketCenterAngle = commonStartAngle;
    const giftSpawnSpacing = 170;
    const introOffsetY = 700;
    const standStillY = 250;
    const startRotation = 0;
    const ticketInTime = 3000;
    const ticketIntroTime = 1000;
    const flyInDuration = 2000;

    const baseRotation = this.wheel!.rotation;
    const expandProxy = {
      size: 0,
      rotation: baseRotation
    };
    const shrinkOtherTicketsTween = new Tween(expandProxy)
      .to({
        size: giftTargets.length,
        rotation: baseRotation
      }, flyInDuration)
      .delay(ticketInTime)
      .easing(Easing.Quadratic.InOut)
      .onUpdate(() => {
        const gapUnitAngle = (Math.PI * 2.0) / (numTicketsBefore + giftTargets.length);
        const activeWedgesAngle = ((Math.PI * 2.0) - (expandProxy.size * gapUnitAngle)) / numTicketsBefore;
        const allTickets = this.getDrawnTicketsInOrder();
        const fullOpenGaps = Math.floor(expandProxy.size);
        const partialGapProgress = expandProxy.size - fullOpenGaps;
        const gapBeforeIndex = new Map<number, number>();

        giftTargets.forEach((target, index) => {
          if (index < fullOpenGaps) {
            gapBeforeIndex.set(target.insertionIndex, (gapBeforeIndex.get(target.insertionIndex) || 0) + 1);
            return;
          }
          if (index === fullOpenGaps && partialGapProgress > 0) {
            gapBeforeIndex.set(
              target.insertionIndex,
              (gapBeforeIndex.get(target.insertionIndex) || 0) + partialGapProgress
            );
          }
        });

        let startAngle = commonStartAngle;

        allTickets.forEach((drawnTicket, index) => {
          startAngle += (gapBeforeIndex.get(index) || 0) * gapUnitAngle;
          drawnTicket.wedge.resize(startAngle, activeWedgesAngle);
          drawnTicket.line.vertices[0].set(0, 0);
          drawnTicket.line.vertices[1].set(
            Math.cos(startAngle) * parameters.wheelRadius,
            Math.sin(startAngle) * parameters.wheelRadius
          );
          startAngle += activeWedgesAngle;
        });
      })
      .start();
    const targetRadiusFactor = 0.5 / Math.cos(targetTicketSize / 2);
    const startCenterAngle = startRotation + ticketCenterAngle;
    const targetGapAngle = (Math.PI * 2.0) / (numTicketsBefore + giftTargets.length);
    const targetActiveWedgesAngle = ((Math.PI * 2.0) - (giftTargets.length * targetGapAngle)) / numTicketsBefore;
    const slotAnglesByInsertionIndex = new Map<number, number[]>();

    let targetStartAngle = commonStartAngle;
    for (let insertionIndex = 0; insertionIndex <= numTicketsBefore; insertionIndex += 1) {
      const giftCountAtIndex = giftTargets.filter(target => target.insertionIndex === insertionIndex).length;

      for (let giftIndex = 0; giftIndex < giftCountAtIndex; giftIndex += 1) {
        const slotAngle = baseRotation + targetStartAngle + (targetGapAngle / 2);
        const slotAngles = slotAnglesByInsertionIndex.get(insertionIndex) || [];
        slotAngles.push(slotAngle);
        slotAnglesByInsertionIndex.set(insertionIndex, slotAngles);
        targetStartAngle += targetGapAngle;
      }

      if (insertionIndex < numTicketsBefore) {
        targetStartAngle += targetActiveWedgesAngle;
      }
    }
    const giftTweens: Tween<any>[] = [];

    giftTargets.forEach((target, index) => {
      const startX = (index - ((giftTargets.length - 1) / 2)) * giftSpawnSpacing;
      const startY = standStillY + introOffsetY;
      const targetPlayerIndex = this.allPlayers.findIndex(player => player.playerId === target.playerId);
      const targetPlayer = this.allPlayers[targetPlayerIndex];
      const ticket = new Ticket(this.two,
        ticketCenterAngle - (startTicketSize / 2),
        startTicketSize,
        targetPlayer.color,
        targetPlayer.playerName,
        true,
        targetPlayer.playerId).massCentered();
      const wrappedTicket = new WrappedTicket(
        this.two,
        ticketCenterAngle - (startTicketSize / 2),
        startTicketSize,
      ).massCentered();
      this.scene!.add(ticket, wrappedTicket);
      ticket.setTranslationShifted(this.wheelGroup!.translation.x, this.wheelGroup!.translation.y);
      ticket.translation.set(startX, startY);
      ticket.rotation = startRotation;
      wrappedTicket.setTranslationShifted(this.wheelGroup!.translation.x, this.wheelGroup!.translation.y);
      wrappedTicket.translation.set(startX, startY);
      wrappedTicket.rotation = startRotation;

      const slotAnglesForTarget = slotAnglesByInsertionIndex.get(target.insertionIndex) || [];
      const slotAngle = slotAnglesForTarget.shift()!!;
      slotAnglesByInsertionIndex.set(target.insertionIndex, slotAnglesForTarget);

      const targetX = this.wheelGroup!.translation.x + Math.cos(slotAngle) * (parameters.wheelRadius * targetRadiusFactor);
      const targetY = this.wheelGroup!.translation.y + Math.sin(slotAngle) * (parameters.wheelRadius * targetRadiusFactor);

      const settleTween = new Tween({ opacity: 1 })
        .to({ opacity: 1 }, 2000)
        .delay((giftTargets.length-index-1)*1000)
        .onComplete(() => {
          ticket.destroy();
          ticket.remove();
          wrappedTicket.destroy();
          wrappedTicket.remove();
          this.scene!.remove(wrappedTicket, ticket);

          if (index === giftTargets.length - 1) {
            this.onAnimationFinished(roundNumber);
          }
        });

      const flyInTween = new Tween({
        x: startX,
        y: standStillY,
        rotation: startCenterAngle,
        size: startTicketSize
      })
        .to({
          x: targetX,
          y: targetY,
          rotation: slotAngle,
          size: targetTicketSize
        }, flyInDuration)
        .easing(Easing.Cubic.Out)
        .onUpdate(proxy => {
          ticket.translation.x = proxy.x;
          ticket.translation.y = proxy.y;
          ticket.rotation = proxy.rotation - ticketCenterAngle;
          ticket.resize(ticketCenterAngle - (proxy.size / 2), proxy.size);
          wrappedTicket.translation.x = proxy.x;
          wrappedTicket.translation.y = proxy.y;
          wrappedTicket.rotation = proxy.rotation - ticketCenterAngle;
          wrappedTicket.resize(ticketCenterAngle - (proxy.size / 2), proxy.size);
        })
        .chain(settleTween);

      const fadeTween = new Tween({ opacity: 1 })
        .delay(1000* index)
        .to({ opacity: 0 }, 1000)
        .onUpdate(proxy => {
          wrappedTicket.opacity = proxy.opacity;
        })
        .chain(flyInTween);

      const standStillTween = new Tween({})
        .to({}, ticketInTime - ticketIntroTime)
        .chain(fadeTween);

      const moveInTween = new Tween({ y: startY })
        .to({ y: standStillY }, ticketIntroTime)
        .easing(Easing.Cubic.Out)
        .onUpdate(proxy => {
          ticket.translation.y = proxy.y;
          wrappedTicket.translation.y = proxy.y;
        })
        .chain(standStillTween);

      moveInTween.start();

      giftTweens.push(flyInTween, fadeTween, settleTween, standStillTween, moveInTween);
    });

    this.tweens.add(shrinkOtherTicketsTween, ...giftTweens);
  }

  animateResurrection(event: ReAppearEvent, roundNumber: number) {
    if (!this.scene || !this.wheel) {
      this.onAnimationFinished(roundNumber);
      return;
    }

    const playersBefore: WheelPlayerWithTickets[] = this.mapPlayersWithTickets(this.allPlayers, this.remainingPlayers);
    const numTicketsBefore = getNumTickets(playersBefore);
    if (numTicketsBefore <= 0) {
      this.onAnimationFinished(roundNumber);
      return;
    }

    const targetPlayerIndex = this.allPlayers.findIndex(player => player.playerId === event.playerId);
    if (targetPlayerIndex < 0) {
      this.onAnimationFinished(roundNumber);
      return;
    }
    const targetPlayer = this.allPlayers[targetPlayerIndex];
    const ticketsBeforeTargetPlayer = playersBefore
      .slice(0, targetPlayerIndex)
      .reduce((sum, player) => sum + Number(player.tickets), 0);
    const targetPlayerTicketsBefore = Number(playersBefore[targetPlayerIndex]?.tickets || 0);
    const insertionIndex = ticketsBeforeTargetPlayer + targetPlayerTicketsBefore;

    const startTicketSize = Math.PI * 0.23;
    const targetTicketSize = (Math.PI * 2.0) / (numTicketsBefore + 1);
    const ticketCenterAngle = commonStartAngle;
    const ticket = new Ticket(
      this.two,
      ticketCenterAngle - (startTicketSize / 2),
      startTicketSize,
      targetPlayer.color,
      targetPlayer.playerName,
      true,
      targetPlayer.playerId
    ).massCentered();
    this.scene.add(ticket);
    ticket.setTranslationShifted(this.wheelGroup!.translation.x, this.wheelGroup!.translation.y);

    ticket.visible = false;
    ticket.scale = 1;

    const startX = 0;
    const startY = 0;
    const startRotation = 0;
    ticket.translation.set(startX, startY);
    ticket.rotation = startRotation;

    const coffinMoveTime = 2000;
    const coffinOpenTime = 5000;
    const coffin = new Coffin(this.two, this.scene);
    this.scene.add(coffin);
    coffin.translation.set(0, 300);

    const coffinTweens = coffin.animateIn(coffinMoveTime, coffinOpenTime);
    this.tweens.add(...coffinTweens);
    const baseRotation = this.wheel!.rotation;
    const expandProxy = {
      size: 0,
      rotation: baseRotation
    };
    const shrinkOtherTicketsTween = new Tween(expandProxy)
      .to({
        size: 1,
        rotation: baseRotation
      }, 500)
      .delay(coffinMoveTime+coffinOpenTime)
      .easing(Easing.Quadratic.InOut)
      .onUpdate(() => {
        const gapAngle = (expandProxy.size * Math.PI * 2.0) / (numTicketsBefore + 1);
        const activeWedgesAngle = ((Math.PI * 2.0) - gapAngle) / numTicketsBefore;
        const allTickets = this.getDrawnTicketsInOrder();

        allTickets.forEach((drawnTicket, index) => {
          const startAngle = commonStartAngle + (index * activeWedgesAngle) + (index >= insertionIndex ? gapAngle : 0);
          drawnTicket.wedge.resize(startAngle, activeWedgesAngle);
          drawnTicket.line.vertices[0].set(0, 0);
          drawnTicket.line.vertices[1].set(
            Math.cos(startAngle) * parameters.wheelRadius,
            Math.sin(startAngle) * parameters.wheelRadius
          );
        });
      })
      .start();

    const settleTween = new Tween({ opacity: 1 })
      .to({ opacity: 1 }, 300)
      .onComplete(() => {
        ticket.destroy();
        ticket.remove();
        this.onAnimationFinished(roundNumber);
      });

    const targetGapAngle = (Math.PI * 2.0) / (numTicketsBefore + 1);
    const targetActiveWedgesAngle = ((Math.PI * 2.0) - targetGapAngle) / numTicketsBefore;
    const slotStartAngle = commonStartAngle + (insertionIndex * targetActiveWedgesAngle);
    const slotAngle = baseRotation + slotStartAngle + (targetGapAngle / 2);
    const targetRadiusFactor = 0.5 / Math.cos(targetTicketSize / 2);
    const targetX = this.wheelGroup!.translation.x + Math.cos(slotAngle) * (parameters.wheelRadius * targetRadiusFactor);
    const targetY = this.wheelGroup!.translation.y + Math.sin(slotAngle) * (parameters.wheelRadius * targetRadiusFactor);
    const startCenterAngle = startRotation + ticketCenterAngle;

    const flyInTween = new Tween({
      x: startX,
      y: startY,
      rotation: startCenterAngle,
      size: startTicketSize
    })
      .to({
        x: targetX,
        y: targetY,
        rotation: slotAngle,
        size: targetTicketSize
      }, 2000)
      .easing(Easing.Cubic.Out)
      .onUpdate(proxy => {
        ticket.translation.x = proxy.x;
        ticket.translation.y = proxy.y;
        ticket.rotation = proxy.rotation - ticketCenterAngle;
        ticket.resize(ticketCenterAngle - (proxy.size / 2), proxy.size);
      })
      .chain(settleTween);

    const standStillTween = new Tween({})
      .to({ }, coffinOpenTime)
      .chain(flyInTween);

    const waitUntilVisibleTween = new Tween({})
      .to({ }, coffinMoveTime)
      .onComplete(p =>{
        ticket.visible = true;
      })
      .chain(standStillTween)
      .start();


    const moveWheeInTween = new Tween({y:-500, opacity: 0})
      .to({ y:0, opacity: 1},1000)
      .delay(-1000 + coffinMoveTime + coffinOpenTime)
      .onComplete(()=> {
        this.ticker!!.visible=true;
      })
      .onUpdate(proxy => {
        this.outerWheelGroup!!.translation.y = proxy.y;
        this.outerWheelGroup!!.opacity=proxy.opacity;
      });

    const moveWheelOutTween = new Tween({y:0, opacity: 1})
      .to({y:-500, opacity:0},1000)
      .onStart(()=> {
        this.ticker!!.visible=false;
      })
      .onUpdate(proxy => {
        this.outerWheelGroup!!.translation.y = proxy.y;
        this.outerWheelGroup!!.opacity=proxy.opacity;
      })
      .chain(moveWheeInTween)
      .start();

    this.tweens.add(moveWheelOutTween, moveWheeInTween, flyInTween, settleTween, shrinkOtherTicketsTween, standStillTween, waitUntilVisibleTween);
  }

  animateRound(event: WheelEvent, roundNumber: number, isBehind: boolean) {
    this.lastAnimationStarted = roundNumber;
    switch (event.type) {
      case 'out':
        this.animateElimination(event, roundNumber, isBehind);
        break;
      case 'in':
        this.animateResurrection(event, roundNumber);
        break;
      case 'gifts':
        this.animateGift(event, roundNumber);
        break;
      default:
        throw Error(`Invalid event ${event}`);
    }
  }
}
